/**
 * Pure TEA update for entity arc moment create/edit dialog.
 *
 * Shell: `EntityArcPanel.vue` (`useTea` + persist Cmds into active fields).
 */
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export const MANUSCRIPT_SCENE_NONE_VALUE = "";

export type EntityArcDialogModel = {
  readonly open: boolean;
  readonly editingMomentId: Maybe<string>;
  readonly at: string;
  readonly text: string;
  readonly sceneLink: string;
};

export const initialEntityArcDialogModel: EntityArcDialogModel = {
  open: false,
  editingMomentId: nothing,
  at: "",
  text: "",
  sceneLink: MANUSCRIPT_SCENE_NONE_VALUE,
};

export type EntityArcDialogMsg =
  | { readonly type: "OpenCreate" }
  | {
      readonly type: "OpenEdit";
      readonly momentId: string;
      readonly at: string;
      readonly text: string;
      readonly sceneLink: string;
    }
  | { readonly type: "Close" }
  | { readonly type: "AtChanged"; readonly at: string }
  | { readonly type: "TextChanged"; readonly text: string }
  | { readonly type: "SceneLinkChanged"; readonly sceneLink: string }
  | { readonly type: "Commit" }
  | { readonly type: "RemoveEditing" }
  | { readonly type: "MomentRemoved"; readonly momentId: string };

export type EntityArcDialogCmd =
  | {
      readonly kind: "persistMoment";
      readonly editingMomentId: Maybe<string>;
      readonly at: string;
      readonly text: string;
      readonly sceneLink: string;
    }
  | { readonly kind: "removeMoment"; readonly momentId: string };

const closed = initialEntityArcDialogModel;

export function updateEntityArcDialog(
  model: EntityArcDialogModel,
  msg: EntityArcDialogMsg,
): readonly [EntityArcDialogModel, readonly EntityArcDialogCmd[]] {
  switch (msg.type) {
    case "OpenCreate":
      return [
        {
          open: true,
          editingMomentId: nothing,
          at: "",
          text: "",
          sceneLink: MANUSCRIPT_SCENE_NONE_VALUE,
        },
        [],
      ];
    case "OpenEdit":
      return [
        {
          open: true,
          editingMomentId: just(msg.momentId),
          at: msg.at,
          text: msg.text,
          sceneLink: msg.sceneLink,
        },
        [],
      ];
    case "Close":
      return [closed, []];
    case "AtChanged":
      return [{ ...model, at: msg.at }, []];
    case "TextChanged":
      return [{ ...model, text: msg.text }, []];
    case "SceneLinkChanged":
      return [{ ...model, sceneLink: msg.sceneLink }, []];
    case "Commit": {
      const text = model.text.trim();
      if (text.length === 0) return [model, []];
      return [
        closed,
        [
          {
            kind: "persistMoment",
            editingMomentId: model.editingMomentId,
            at: model.at.trim(),
            text,
            sceneLink: model.sceneLink.trim(),
          },
        ],
      ];
    }
    case "RemoveEditing":
      if (isNothing(model.editingMomentId)) return [model, []];
      return [
        model,
        [{ kind: "removeMoment", momentId: model.editingMomentId.value }],
      ];
    case "MomentRemoved":
      if (
        isJust(model.editingMomentId) &&
        model.editingMomentId.value === msg.momentId
      ) {
        return [closed, []];
      }
      return [model, []];
    default:
      return assertNever(msg);
  }
}

export const updateEntityArcDialogFn: UpdateFn<
  EntityArcDialogModel,
  EntityArcDialogMsg,
  EntityArcDialogCmd
> = updateEntityArcDialog;
